/**
 * DashboardNoticeCard - 대시보드(실시간 현황) 상단 공지
 *
 * ⭐ 2026-07-27 개편: 공지가 쌓일수록 카드가 그만큼 세로로 쌓여(4건=화면 절반) 정작
 *   '오늘의 현황'이 밀려났다. → **한 줄 요약 바 1개**로 접고, 누르면 그 안에서 전체 목록을
 *   쭉 훑어볼 수 있게(각 항목 클릭 시 본문 펼침). 지난 공지 전체는 고객지원 > 공지사항.
 *
 * ⭐ 2026-10-08 대표 지시: 가로로 두 칸 — 왼쪽 '중요 공지'(본부가 중요 체크한 것), 오른쪽 '일반 공지'.
 *   (같은 날 세로 길이 축소 — 칸 안에 제목 약 3줄, 대표 지시)
 *   칸마다 최근 제목이 보이고 누르면 그 자리에서 본문이 펼쳐진다. 길어지면 칸 안에서 스크롤
 *   (칸 높이를 묶어 '오늘의 현황'이 밀리지 않게 — 7/27 개편 이유 유지). 좁은 화면에선 위아래로.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../../config/api';

interface Notice {
  id: string;
  title: string;
  body: string;
  version: string | null;
  show_popup: boolean;
  show_dashboard: boolean;
  is_important?: boolean;
  updated_at: string | null;
}

const getToken = () =>
  localStorage.getItem('auth_access_token') ||
  localStorage.getItem('accessToken') ||
  localStorage.getItem('token');

const formatDate = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

const NoticeColumn: React.FC<{
  kind: 'important' | 'normal';
  notices: Notice[];
  openId: string | null;
  setOpenId: (id: string | null) => void;
}> = ({ kind, notices, openId, setOpenId }) => {
  const imp = kind === 'important';
  return (
    <div
      className={`rounded-lg border overflow-hidden flex flex-col ${
        imp ? 'border-red-200 bg-red-50' : 'border-indigo-200 bg-indigo-50'
      }`}
    >
      <div className="flex items-center gap-2 px-4 py-1.5">
        <span className="flex-shrink-0">{imp ? '🔴' : '📢'}</span>
        <span className={`font-semibold flex-shrink-0 ${imp ? 'text-red-900' : 'text-indigo-900'}`}>
          {imp ? '중요 공지' : '일반 공지'}
        </span>
        <span
          className={`flex-shrink-0 text-xs font-semibold text-white rounded-full px-2 py-0.5 ${
            imp ? 'bg-red-500' : 'bg-indigo-500'
          }`}
        >
          {notices.length}
        </span>
      </div>
      <div className="bg-white border-t border-gray-100 max-h-28 overflow-y-auto flex-1">
        {notices.length === 0 ? (
          <p className="px-4 py-2 text-sm text-gray-400">
            {imp ? '지금 중요 공지가 없습니다.' : '공지가 없습니다.'}
          </p>
        ) : (
          notices.map((n) => {
            const open = openId === n.id;
            return (
              <div key={n.id} className="border-b border-gray-100 last:border-b-0">
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : n.id)}
                  className="w-full flex items-center gap-2 px-4 py-1.5 text-left hover:bg-gray-50"
                >
                  <span className={`text-sm truncate ${imp ? 'font-semibold text-gray-900' : 'text-gray-800'}`}>
                    {n.title}
                  </span>
                  <span className="text-xs text-gray-400 flex-shrink-0 ml-auto">{formatDate(n.updated_at)}</span>
                  <span className="text-gray-400 flex-shrink-0 text-xs">{open ? '▲' : '▼'}</span>
                </button>
                {open && (
                  <div className="px-4 pb-3">
                    <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{n.body}</p>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const DashboardNoticeCard: React.FC = () => {
  const navigate = useNavigate();
  const [notices, setNotices] = useState<Notice[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const token = getToken();
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/api/v1/owner/notices/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setNotices((data.notices || []).filter((n: Notice) => n.show_dashboard));
      } catch {
        /* 무시 */
      }
    })();
  }, []);

  if (notices.length === 0) return null;

  const important = notices.filter((n) => n.is_important);
  const normal = notices.filter((n) => !n.is_important);

  return (
    <div className="mb-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <NoticeColumn kind="important" notices={important} openId={openId} setOpenId={setOpenId} />
        <NoticeColumn kind="normal" notices={normal} openId={openId} setOpenId={setOpenId} />
      </div>
      <div className="text-right mt-1">
        <button
          type="button"
          onClick={() => navigate('/owner/notices')}
          className="text-xs text-indigo-600 hover:underline"
        >
          지난 공지 전체보기 →
        </button>
      </div>
    </div>
  );
};

export default DashboardNoticeCard;
